import { useMemo } from "react";
import { Control, Controller, UseFormWatch, UseFormTrigger } from "react-hook-form";
import TextField from "@/components/TextField";
import MenuDropdown from "@/components/MenuDropdown";
import Select from "@/components/Select";
import DateSelect from "@/components/DateSelect";
import Banner from "@/components/Banner";
import { DateValueType } from "react-tailwindcss-datepicker";
import { NotePriorityValues, NotePriority } from "@/types";
import { toFormat } from "@/lib/dateHelpers";
import {
  SUSPENSION_REASON_OPTIONS,
  NOTE_PRIORITY_OPTIONS,
  SuspensionFormValues,
  SuspensionReasonOption
} from "./suspensionFormConstants";

interface SuspensionFormProps {
  control: Control<SuspensionFormValues>;
  watch: UseFormWatch<SuspensionFormValues>;
  trigger: UseFormTrigger<SuspensionFormValues>;
  isEditMode?: boolean;
  testIdPrefix?: string;
  suspensionReasonOptions?: SuspensionReasonOption[];
  /** Number of other active suspensions (excluding the one being edited) */
  otherActiveSuspensionCount?: number;
  /** Whether the suspension being edited is currently active (for edit mode) */
  isSelectedSuspensionActive?: boolean;
}

export default function SuspensionForm({
  control,
  watch,
  trigger,
  isEditMode = false,
  testIdPrefix = "suspension",
  suspensionReasonOptions = SUSPENSION_REASON_OPTIONS,
  otherActiveSuspensionCount = 0,
  isSelectedSuspensionActive = false
}: SuspensionFormProps) {
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  // Show description if person is Active and the suspension will take effect now
  const willChangeStatusToSuspended = useMemo(() => {
    if (!startDate) return false;

    const today = new Date().toISOString().split("T")[0];
    const startDateIsNowOrPast = startDate <= today;
    const endDateIsEmptyOrFuture = !endDate || endDate >= today;

    return startDateIsNowOrPast && endDateIsEmptyOrFuture;
  }, [startDate, endDate]);

  // Show status change banner in EDIT mode when ending an active suspension
  const showStatusChangeBanner = useMemo(() => {
    if (!isEditMode) return false;

    // Only show if the suspension being edited is currently active
    if (!isSelectedSuspensionActive) return false;

    // Show banner when an end date is set
    return !!endDate;
  }, [isEditMode, isSelectedSuspensionActive, endDate]);

  // Show banner about status change when ending a suspension
  const willBecomeActive = showStatusChangeBanner && otherActiveSuspensionCount === 0;
  const willRemainSuspended = showStatusChangeBanner && otherActiveSuspensionCount > 0;

  // Check if the end date is in the future and format it for display
  const endDateIsFuture = useMemo(() => {
    if (!endDate) return false;
    const today = new Date().toISOString().split("T")[0];
    return endDate > today;
  }, [endDate]);

  return (
    <div className="flex flex-col gap-6" data-testid={`${testIdPrefix}-form`}>
      {willChangeStatusToSuspended && !isEditMode && (
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          Suspending this person will change their status from Active to Suspended. They will be notified about this
          change.
        </p>
      )}

      <input autoFocus hidden />

      <Controller
        name="suspensionReason"
        control={control}
        rules={{ required: "Suspension reason is required" }}
        render={({ field: { onChange, value } }) => (
          <MenuDropdown
            options={suspensionReasonOptions}
            value={suspensionReasonOptions.find((r) => r.value === value)}
            onSelect={(option) => onChange(option.value)}
            label="Suspension Reason *"
            getLabel={(option) => `${option.label} (${option.code})`}
            disabled={isEditMode}
            renderItem={(option) => (
              <div
                className="flex items-start gap-3 w-full"
                data-testid={`${testIdPrefix}-reason-option-${option.value}`}
              >
                <div className="flex-1">
                  <div className="text-body-large">
                    {option.label} ({option.code})
                  </div>
                </div>
              </div>
            )}
          />
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="startDate"
          control={control}
          rules={{
            required: "Start Date is required",
            validate: (value) => {
              if (!value) return "Start Date is required";
              const currentEndDate = watch("endDate");
              if (currentEndDate && value > currentEndDate) {
                return "Start date cannot be later than end date";
              }
              return true;
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <DateSelect
              label="Start Date *"
              value={{ startDate: field.value, endDate: field.value }}
              onChange={(x: DateValueType | string) => {
                if (typeof x === "string") return;
                if (x === null) {
                  field.onChange("");
                  trigger("endDate");
                  return;
                }
                field.onChange(x.startDate);
                trigger("endDate");
              }}
              onBlur={field.onBlur}
              useSingleDate
              useMultiCalendar={false}
              useModal
              error={!!error}
              errorMessage={error?.message}
            />
          )}
        />
        <Controller
          name="endDate"
          control={control}
          rules={{
            validate: (value) => {
              if (!value) return true;
              const currentStartDate = watch("startDate");
              if (currentStartDate && value < currentStartDate) {
                return "End date cannot be earlier than start date";
              }
              return true;
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <DateSelect
              label="End Date"
              value={{ startDate: field.value, endDate: field.value }}
              onChange={(x: DateValueType | string) => {
                if (typeof x === "string") return;
                if (x === null) {
                  field.onChange("");
                  trigger("startDate");
                  return;
                }
                field.onChange(x.startDate);
                trigger("startDate");
              }}
              onBlur={field.onBlur}
              useSingleDate
              useMultiCalendar={false}
              useModal
              error={!!error}
              errorMessage={error?.message}
            />
          )}
        />
      </div>

      {willBecomeActive && (
        <Banner
          inline
          variant="info"
          description=""
          subText={
            endDateIsFuture
              ? `This person's membership status will change from Suspended to Active on ${toFormat(endDate)} if there are no additional suspensions. The person will be notified about the change.`
              : "This person's membership status will change from Suspended to Active. The person will be notified about the change."
          }
        />
      )}

      {willRemainSuspended && (
        <Banner
          inline
          variant="info"
          description=""
          subText="This person will remain Suspended due to another active suspension(s)."
        />
      )}

      <Controller
        name="note"
        control={control}
        render={({ field: { onChange, value } }) => (
          <TextField
            type="textarea"
            rows={4}
            label="Note"
            value={value || ""}
            onChangeText={onChange}
            className="w-full"
          />
        )}
      />

      <Controller
        name="notePriority"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            label="Note Priority"
            options={NOTE_PRIORITY_OPTIONS}
            value={value ?? NotePriorityValues.NONE}
            onSelect={(val) => onChange((val || NotePriorityValues.NONE) as NotePriority)}
            className="w-full"
          />
        )}
      />
    </div>
  );
}
